import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Popover, Switch } from 'antd';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useReferenceData } from '../../../../hooks/use_reference_data';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { getSpeciesTop } from '../../../../redux/reference_data';
import { ExtendedMapQueryParams } from '../../../../routes/map_page/lib/map_page_types';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { LunkerDescription } from '../lakes_modal/tab_species/species_detail';
import { SvgLunker } from '../lakes_modal/tab_species/svg_lunker';
import styles from './pin_filters_modal.less';

interface PinFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLunkerClick: (lunker: boolean) => void;
  onSpeciesClick: (speciesName: string | null) => void;
  onSeasonGroupClick: (seasonGroupName: string | null) => void;
  onClearAll: () => void;
}

export const PinFiltersModal = (props: PinFiltersModalProps) => {
  const { isOpen, onClose, onSpeciesClick, onSeasonGroupClick, onLunkerClick, onClearAll } = props;

  const { allSeasonGroups } = useReferenceData();
  const topSpecies = useSelector(getSpeciesTop);
  const { getCurrentQuery } = useQueryString();
  const query = getCurrentQuery<ExtendedMapQueryParams>();
  const queryLunker = !!query.lunker;
  const querySpecies = query.species;
  const querySeasonGroup = query.season_group;

  const querySpeciesFull = querySpecies ? topSpecies.find((s) => s.name === querySpecies) : null;
  const querySeasonGroupFull = querySeasonGroup
    ? allSeasonGroups.find((sg) => sg.name === querySeasonGroup)
    : null;

  useEffect(() => {
    if (isOpen) {
      WebAnalytics.mapFilterModalRendered();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={classNames(styles.modal)}>
      <div className={styles.modalHeader}>
        <span className={styles.heading}>Filter Map By</span>
        <CloseOutlined onClick={onClose} />
      </div>
      <div className={styles.modalContent}>
        <section>
          <div className="flex justify-between items-baseline mb-1">
            <span className="font-bold text-lg">Species</span>
            {querySpeciesFull && (
              <span
                className="cursor-pointer bg-white px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                onClick={() => {
                  onSpeciesClick(null);
                }}
              >
                {querySpeciesFull.display_name}
                <CloseOutlined />
              </span>
            )}
          </div>
          <div>
            <fieldset className="grid grid-cols-3 gap-2">
              {topSpecies.map((s) => {
                const selected = querySpecies === s.name;
                return (
                  <label
                    htmlFor={s.name}
                    key={s.name}
                    className={classNames(
                      'border p-3 rounded-xl flex flex-col gap-2 items-center cursor-pointer text-center bg-white select-none',
                      {
                        outline: selected,
                        'outline-2': selected,
                        'outline-black': selected,
                      }
                    )}
                    onClick={(e) => {
                      if (selected) {
                        onSpeciesClick(null);
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <img src={s.image} alt={s.img_alt} />
                    <span className="text-xs">{s.display_name}</span>
                    <input
                      className={'hidden'}
                      type="radio"
                      id={s.name}
                      name="species"
                      value={s.name}
                      checked={selected}
                      onChange={(e) => {
                        onSpeciesClick(e.target.value);
                      }}
                    />
                  </label>
                );
              })}
            </fieldset>
          </div>
        </section>

        <section>
          <div className="flex justify-between items-baseline mb-1">
            <span className="font-bold text-lg">Fishing Potential</span>
            {queryLunker && (
              <span
                className="cursor-pointer bg-white px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                onClick={() => {
                  onLunkerClick(false);
                }}
              >
                Lunkers
                <CloseOutlined />
              </span>
            )}
          </div>
          <label className="border rounded p-2 bg-white cursor-pointer flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <SvgLunker className="text-[#8800FF]" />
              <span className="font-bold text-base">
                Lunker Potential Lakes (MN only){' '}
                <Popover content={LunkerDescription} trigger="hover" overlayClassName="w-96">
                  <QuestionCircleOutlined className="opacity-70" />
                </Popover>
              </span>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <span>Surveyed waters known to hold giants. Cast with confidence.</span>
              </div>
              <div>
                <Switch
                  checked={queryLunker}
                  onClick={(checked) => {
                    onLunkerClick(checked);
                  }}
                />
              </div>
            </div>
          </label>
        </section>

        <section>
          <div className="flex justify-between items-baseline mb-1">
            <span className="font-bold text-lg">Season</span>
            {querySeasonGroupFull && (
              <span
                className="cursor-pointer bg-white px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                onClick={() => {
                  onSeasonGroupClick(null);
                }}
              >
                {querySeasonGroupFull.display_name}
                <CloseOutlined />
              </span>
            )}
          </div>
          <div>
            <fieldset className="flex flex-wrap gap-2">
              {allSeasonGroups.map((sg) => {
                const selected = querySeasonGroup === sg.name;
                return (
                  <label
                    htmlFor={sg.name}
                    key={sg.name}
                    className={classNames('border px-2 py-1 rounded-full cursor-pointer bg-white', {
                      outline: selected,
                      'outline-2': selected,
                      'outline-black': selected,
                    })}
                  >
                    <span
                      className="text-xs select-none"
                      onClick={(e) => {
                        if (selected) {
                          onSeasonGroupClick(null);
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      {sg.display_name}
                    </span>
                    <input
                      className={'hidden'}
                      type="radio"
                      id={sg.name}
                      value={sg.name}
                      name="season_group"
                      checked={selected}
                      onChange={(e) => {
                        onSeasonGroupClick(e.target.value);
                      }}
                    />
                  </label>
                );
              })}
            </fieldset>
          </div>
        </section>
      </div>
      <div className="flex justify-between items-center p-3 border-t">
        <OmniaButton kind="text" size="md" onPress={onClearAll}>
          Clear All
        </OmniaButton>
        <OmniaButton kind="secondary" size="md" onPress={onClose}>
          See Results
        </OmniaButton>
      </div>
    </div>
  );
};
